import { ACTIVITY_FACTOR, KCAL_PER_G, KCAL_SURPLUS, MACRO_SPLIT, PROGRAM_WEEKS, PROTEIN_PER_KG, SLEEP_HOURS } from '@/data/constants';
import type { DayOfWeek, Profile, Sex } from '@/types';

/** Indice de masse corporelle */
export const calcBMI = (weightKg: number, heightCm: number): number => {
  if (!weightKg || !heightCm) return 0;
  return weightKg / Math.pow(heightCm / 100, 2);
};

export type BMICategory = {
  label: 'Insuffisant' | 'Normal' | 'Surpoids' | 'Obésité';
  /** Classe Tailwind pour la couleur d'affichage */
  color: string;
};

export const bmiCategory = (bmi: number): BMICategory => {
  if (bmi < 18.5) return { label: 'Insuffisant', color: 'text-amber-400' };
  if (bmi < 25)   return { label: 'Normal',     color: 'text-emerald-400' };
  if (bmi < 30)   return { label: 'Surpoids',   color: 'text-amber-400' };
  return            { label: 'Obésité', color: 'text-rose-400' };
};

/**
 * Métabolisme de base + activité (Mifflin-St Jeor).
 * Renvoie les calories de maintenance arrondies.
 */
export const calcTDEE = (
  weightKg: number,
  heightCm: number,
  age: number,
  sex: Sex,
  activityFactor: number = ACTIVITY_FACTOR,
): number => {
  const bmr = sex === 'h'
    ? 10 * weightKg + 6.25 * heightCm - 5 * age + 5
    : 10 * weightKg + 6.25 * heightCm - 5 * age - 161;
  return Math.round(bmr * activityFactor);
};

/** Calories cibles pour prise de masse sèche (TDEE + surplus) */
export const calcKcalTarget = (tdee: number): number => tdee + KCAL_SURPLUS;

/** Cibles macros en grammes basées sur les calories cibles */
export const calcMacroTargets = (kcalTarget: number, weightKg: number): { protein: number; carbs: number; fat: number } => ({
  protein: Math.round(weightKg * PROTEIN_PER_KG),
  carbs:   Math.round((kcalTarget * MACRO_SPLIT.carbs) / KCAL_PER_G.carbs),
  fat:     Math.round((kcalTarget * MACRO_SPLIT.fat)   / KCAL_PER_G.fat),
});

/**
 * Heure de coucher pour caler N heures de sommeil avant un réveil donné.
 * @param wakeTime "HH:MM" (24h)
 * @param hours    durée de sommeil cible
 * @returns "HH:MM"
 */
export const calcBedtime = (wakeTime: string, hours: number = SLEEP_HOURS): string => {
  const parts = wakeTime.split(':').map(Number);
  const wh = parts[0] ?? 0;
  const wm = parts[1] ?? 0;
  if (Number.isNaN(wh) || Number.isNaN(wm)) return '--:--';
  const total = wh * 60 + wm - hours * 60;
  const norm = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(norm / 60);
  const m = norm % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Convertit le jour JS (0=Dim, 1=Lun, …) vers convention APOLLO (0=Lun, 6=Dim).
 */
export const jsDayToApollo = (jsDay: number): DayOfWeek =>
  ((jsDay + 6) % 7) as DayOfWeek;

/** Jour APOLLO (0=lundi…) du jour passé en paramètre. */
export const dateToApolloDay = (d: Date = new Date()): DayOfWeek =>
  jsDayToApollo(d.getDay());

/**
 * Indice de semaine du programme (1 à PROGRAM_WEEKS) à partir d'une date de démarrage.
 * Au-delà de PROGRAM_WEEKS, reste sur la dernière semaine (rythme de croisière).
 */
export const computeWeekIndex = (programStartDate: string | undefined, now: Date = new Date()): 1 | 2 | 3 | 4 => {
  if (!programStartDate) return 1;
  const start = new Date(programStartDate);
  const diff = Math.floor((now.getTime() - start.getTime()) / (7 * 86400000));
  const idx = Math.max(1, Math.min(diff + 1, PROGRAM_WEEKS));
  return idx as 1 | 2 | 3 | 4;
};

/**
 * Indice de jour APOLLO (0=jour 0 du programme, …, 6=jour 6) en tenant compte
 * du jour de démarrage de l'utilisateur (`startDayOfWeek`).
 *
 * Si l'utilisateur a démarré un mercredi (startDayOfWeek=2), son jour 0 = mercredi.
 * Le jeudi suivant est son jour 1, etc.
 */
export const computeJourIndex = (
  startDayOfWeek: DayOfWeek | undefined,
  now: Date = new Date(),
): DayOfWeek => {
  const today = dateToApolloDay(now);
  const start = startDayOfWeek ?? 0;
  return (((today - start) % 7) + 7) % 7 as DayOfWeek;
};

/**
 * Est-ce un jour de bureau (lundi–vendredi APOLLO) ?
 * Utile pour choisir l'heure de réveil pertinente (wakeOffice vs wakeHome).
 */
export const isOfficeDay = (now: Date = new Date()): boolean => {
  const d = dateToApolloDay(now);
  return d >= 0 && d <= 4;
};

/** Heure de réveil applicable aujourd'hui selon le profil */
export const todaysWakeTime = (profile: Pick<Profile, 'wakeOffice' | 'wakeHome'>, now: Date = new Date()): string =>
  isOfficeDay(now) ? profile.wakeOffice : profile.wakeHome;
